import { parseOriginList } from "@workspace/core/auth/redirect";

const DEFAULT_APP_URL = "http://localhost:9002";
const DEFAULT_API_URL = "http://localhost:3002";

/**
 * Caminhos same-origin servidos por route handlers deste app. Ambos existem
 * pelo mesmo motivo do proxy `/api`: cookie first-party (Safari ITP) e, agora,
 * poder trocar o backend sem rebuild.
 */
const REPORT_API_PROXY_PATH = "/report-api";

/**
 * Origem publica do proprio app.
 *
 * No navegador vem de `window.location.origin` — a pagina ja sabe onde esta, e
 * assim o valor acompanha o dominio automaticamente. Isso e o que permite trocar
 * pre.geoofficepro.com.br por geoofficepro.com.br sem reconstruir a imagem.
 *
 * No servidor cai em `WEB_APP_URL` (sem prefixo NEXT_PUBLIC_, logo lida em
 * runtime). A leitura de `NEXT_PUBLIC_WEB_APP_URL` fica so como ponte para
 * imagens antigas: ela SO funciona se o valor tiver sido passado no build,
 * porque o `next build` substitui a expressao pelo literal. Definir apenas
 * essa variavel em runtime nao tem efeito.
 */
export function getWebsiteDomain(): string {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  return (
    process.env.WEB_APP_URL || process.env.NEXT_PUBLIC_WEB_APP_URL || DEFAULT_APP_URL
  );
}

/**
 * Backend principal. SO e chamada no servidor — o navegador usa `/api/`, que o
 * route handler em `app/api/[...path]` encaminha para ca.
 *
 * `WEB_API_URL` nao leva prefixo NEXT_PUBLIC_ de proposito: o `next build`
 * inlina variaveis com esse prefixo no bundle do cliente E do servidor, entao o
 * valor ficaria congelado na imagem. Sem prefixo, e resolvido em runtime.
 */
export function getApiDomain(): string {
  return (
    process.env.WEB_API_URL || process.env.NEXT_PUBLIC_WEB_API_URL || DEFAULT_API_URL
  );
}

/**
 * License Manager, responsavel pelas rotas do Parse Server.
 *
 * E lida somente no servidor pelo proxy `/api`. Sem o prefixo NEXT_PUBLIC_, o
 * endereco pode ser alterado em runtime e nao e exposto no bundle do browser.
 */
export function getLicenseManagerApiDomain(): string {
  const domain = process.env.LICENSE_MANAGER_API_URL?.trim();

  if (!domain) {
    throw new Error("LICENSE_MANAGER_API_URL is not configured");
  }

  return domain;
}

/**
 * Backend de relatorios. No navegador aponta para o proxy same-origin; no
 * servidor, para o endereco real, lido em runtime.
 */
export function getReportApiDomain(): string {
  if (typeof window !== "undefined") {
    return REPORT_API_PROXY_PATH;
  }

  return (
    process.env.REPORT_API_URL ||
    process.env.NEXT_PUBLIC_REPORT_API_URL ||
    DEFAULT_API_URL
  );
}

// Para cookies cross-subdomain (ex: .s1.solutionsoftwares.com.br)
export function getSessionTokenFrontendDomain(): string | undefined {
  return process.env.NEXT_PUBLIC_SESSION_TOKEN_FRONTEND_DOMAIN;
}

/**
 * Dominio pai sob o qual a sessao vale para todos os subdominios — o que permite
 * web e admin compartilharem o mesmo login.
 *
 * SO e lida no servidor, pelo proxy `/api`, ao reescrever o `Set-Cookie` do
 * backend. Sem prefixo NEXT_PUBLIC_ de proposito: o `next build` congelaria o
 * valor na imagem, e trocar de dominio exigiria rebuild.
 *
 * Deve casar com o `SUPERTOKENS_COOKIEDOMAIN` do backend. Divergencia aqui cria
 * dois cookies de mesmo nome em escopos diferentes — que e justamente o conflito
 * que este valor existe para evitar.
 *
 * Sem valor, o cookie fica host-only: o login continua funcionando, mas nao e
 * compartilhado entre os subdominios.
 */
export function getSessionCookieDomain(): string | undefined {
  return process.env.SESSION_COOKIE_DOMAIN?.trim() || undefined;
}

/**
 * Origens de outros apps (ex.: admin) para onde o login central pode devolver o
 * usuario depois de autenticar. Comparacao exata de origem, sem wildcard.
 *
 * SO e lida no servidor (sem NEXT_PUBLIC_): a pagina de login repassa a lista ao
 * componente cliente, de modo que trocar o dominio do admin nao exige rebuild.
 *
 * Ex.: `http://localhost:9001` local, `https://admin.geoofficepro.com.br` em prod.
 */
export function getAllowedRedirectOrigins(): string[] {
  return parseOriginList(process.env.AUTH_ALLOWED_REDIRECT_ORIGINS);
}

export function getSupertokensConnectionURI(): string {
  return process.env.SUPERTOKENS_CONNECTION_URI ?? "";
}

export function getSupertokensAPIKey(): string {
  return process.env.SUPERTOKENS_API_KEY ?? "";
}

export function getSupertokensAppName(): string {
  return process.env.SUPERTOKENS_APP_NAME ?? "GeoOfficePro Platform";
}

/**
 * Base do ProtectionServer (File Protection Server) da ODA, de onde saem
 * `DrawingWeb.js`, `DrawingWeb.wasm` e o toolkit de edição.
 *
 * O `DrawingWeb.wasm` é artefato licenciado e NAO pode ser servido pelo Next
 * (`apps/web/public`): o ProtectionServer marca `ProtectedFileName` e valida o
 * handshake antes de entregar o binário. Ver docs/oda-dwg-web-platform-techdoc.md §12.2.
 *
 * Como toda `NEXT_PUBLIC_*`, o valor é inlined no build — trocar o domínio do
 * FPS exige rebuild da imagem web, não só reiniciar o container.
 *
 * O default `/fps/` mantém o carregamento same-origin usado em desenvolvimento
 * local, quando os arquivos estão em `apps/web/public/fps` (gitignorado).
 */
export function getOdaFpsBaseUrl(): string {
  // Constante: o /fps/ e servido pelo route handler em app/fps/[...path],
  // que resolve ODA_FPS_PROXY_TARGET em runtime. Nao ha mais valor de cliente
  // a configurar — e por isso nao ha mais nada congelado no build.
  return "/fps/";
}


/**
 * Interruptor de rollout da EDICAO de DWG. Visualizar nao depende dele.
 *
 * Existe separado da feature de plano porque resolve outro problema: a propria
 * ODA classifica o Drawing inWEB como alpha no README do pacote. Se a edicao
 * comecar a corromper desenhos, precisamos desligar para todo mundo sem mexer
 * em plano de ninguem.
 *
 * Editar exige as DUAS coisas: esta flag ligada E a feature EDIT_CAD no plano.
 *
 * Desligado por padrao — habilitar e uma decisao consciente por ambiente.
 */
export function isDwgEditEnabled(): boolean {
  // Truthiness, nao `??`: um ARG declarado sem valor no Dockerfile vira ENV
  // vazio, que `??` deixaria passar.
  return (process.env.NEXT_PUBLIC_DWG_EDIT_ENABLED || "") === "true";
}
